import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ProjectLoadingOverlay from './components/common/ProjectLoadingOverlay';
import { useNavigate } from 'react-router-dom';

// Components - Layout
import PipelineNavigation from './components/layout/PipelineNavigation';
import ContentModal from './components/layout/ContentModal';
import FeedbackButton from './components/common/FeedbackButton';
import logoSource from './assets/dataquadio.png';
import logoSourceInverted from './assets/dataquadio_inverted.png';

// Components - Views (same imports as original App.js)
import DataQualityDashboard from './components/dashboard/DataQualityDashboard';
import DataLoader from './components/dataload/DataLoader';
import DataPreview from './components/datapreview/DataPreview';
import AudioAudit, { detectAudioColumns } from './components/audio/AudioAudit';
import CollectorDashboard from './components/explore/CollectorDashboard';
import ConfigureDetectors from './components/configuration/ConfigureDetectors';
import ConfigureBackChecks from './components/configuration/ConfigureBackChecks';
import ModeSelector from './components/configuration/ModeSelector';
import QuickScanProgress from './components/configuration/QuickScanProgress';
import ExecutiveSummary from './components/configuration/ExecutiveSummary';
import CleaningStudio from './components/cleaning/CleaningStudio';
import CleanedDataReview from './components/cleaning/CleanedDataReview';
import Projects from './components/project_setup/Projects';
import BackcheckDashboard from './components/bcdashboard/BackcheckDashboard';
import DataSummary from './components/datasummary/DataSummary';
import CorrelationMatrix from './components/correlationmatrix/CorrelationMatrix';
import ReportGenerator from './components/reports/ReportGenerator';
import DataExport from './components/dataload/DataExport';

// Services & Utils
import { DataService } from './services/dataService';
import { useTheme } from './utils/ThemeSwitcher';

// Icons for modal headers
import { 
  FolderOpen, FileInput, TableIcon, ListChecks, GitCompare, Users,
  Shield, Settings, Brain, Layout, Zap, Target, Download, FileText,
  Database, Search, CheckCircle2, FileOutput
} from 'lucide-react';

// API Base URL - uses environment variable in production

import { activateProjectStorage } from './utils/localProjects';
let API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com' || 'http://localhost:8000';
export function setAppApiBase(url) { API_BASE_URL = url; }



function App() {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  // ============================================
  // STATE MANAGEMENT (EXACTLY as in App.js)
  // ============================================
  
  const [data, setData] = useState(null);
  const [qualityChecksData, setQualityChecksData] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [currentProject, setCurrentProject] = useState(() => {
    try {
      const saved = localStorage.getItem('dq_current_project');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [backcheckData, setBackcheckData] = useState(null);
  const [backcheckQualityData, setBackcheckQualityData] = useState(null);
  
  // Backcheck config - EXACT structure expected by ConfigureBackChecks
  const [backcheckConfig, setBackcheckConfig] = useState({
    primaryRespondentId: '',
    backcheckRespondentId: '',
    backcheckDataCollectorId: '',
    backcheckInterviewDateId: '',
    type1Variables: [],
    type2Variables: [],
    type3Variables: []
  });
  
  // XLSForm state
  const [xlsformData, setXLSFormData] = useState(null);
  const [xlsformMatchingResult, setXLSFormMatchingResult] = useState(null);
  const [xlsformFileName, setXLSFormFileName] = useState(null);
  
  // State restoration flag
  const [isRestoringState, setIsRestoringState] = useState(false);
  
  // Auto-refresh
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [autoRefreshError, setAutoRefreshError] = useState(null);
  // Sources that failed to refresh this session (e.g. a decommissioned
  // SurveyCTO server): don't hammer them again on every restore/navigation —
  // cached data is the working copy until the user reconnects or syncs manually.
  const autoRefreshFailedRef = useRef(new Set());
  
  // Backcheck loading states - REQUIRED by ConfigureBackChecks
  const [isLoadingBackcheck, setIsLoadingBackcheck] = useState(false);
  const [backcheckError, setBackcheckError] = useState(null);
  const [backcheckResults, setBackcheckResults] = useState(null);
  const [isExpandedBackcheck, setIsExpandedBackcheck] = useState(true);
  
  // Analysis mode
  const [analysisMode, setAnalysisMode] = useState(null);
  const [quickScanResults, setQuickScanResults] = useState(null);
  const [showModeSelector, setShowModeSelector] = useState(true);
  
  // Cleaned data
  const [cleanedData, setCleanedData] = useState(null);
  const [cleaningComplete, setCleaningComplete] = useState(false);
  const [cleanedCheckResults, setCleanedCheckResults] = useState({});
  
  // Report
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Cleaning studio state
  const [cleaningStudioState, setCleaningStudioState] = useState({
    keyVariables: { respondentId: '', collectorId: '', interviewDate: '', surveyUniqueKey: '' },
    selectedIssues: [],
    scriptContent: '',
    consoleOutput: [],
    versions: [],
    currentVersion: null
  });
  
  // Quality checks state - REQUIRED by ConfigureDetectors
  const [selectedChecks, setSelectedChecks] = useState([]);
  const [selectedVariables, setSelectedVariables] = useState({
    duplicates: [],
    missingness: [],
    outliers: [],
    duration: [],
    survey_flow: []
  });
  const [activeQualityTab, setActiveQualityTab] = useState(null);
  const [textClusteringSettings, setTextClusteringSettings] = useState({
    minResponseLength: 1,
    nClusters: null,
    similarityThreshold: 0.85
  });
  const [sentinelSettings, setSentinelSettings] = useState({
    sentinelRegex: '',           // empty = use backend default
    minWords: 15,
    severity: 'medium',
    perVariableConfig: {},       // { variable: { sentinelRegex, minWords, severity } }
    // Optional response-quality detectors (off by default)
    detectGibberish: false,
    detectDkRefused: false,
    detectEncoding: false,
  });
  const [gatedFieldsSettings, setGatedFieldsSettings] = useState({
    defaultMinWords: 15,
    defaultSeverity: 'high',
    rules: [],                   // [{ ruleId, label, followUpVar, gateVar, gateValues, minWords, severity, mode }]
  });
  const [probeDepthSettings, setProbeDepthSettings] = useState({
    minWords:        15,
    scoreThreshold:  35,
    severity:        'medium',
    weights: {                   // tunable composite weights (see probe_depth_check.py)
      noun_phrase: 1.5,
      domain:      6.0,
      units:       8.0,
      generic:     4.0,
    },
    domainTokensExtra: [],       // user-added tokens (combined with backend defaults)
    perVariableConfig: {},
  });
  const [straightLiningSettings, setStraightLiningSettings] = useState({
    varianceThreshold:   0.5,    // per-respondent variance below this → flag
    entropyThreshold:    1.0,    // Shannon entropy below this → flag
    repetitionThreshold: 0.8,    // fraction of repeated values above this → flag
    flagScoreCutoff:     0.5,    // combined severity score above this → flagged
    severity:            'medium',
    methodWeights: null,         // null = backend default (variance/entropy/pattern/iqr/ml_anomaly)
    variables: null,             // null = auto-detect numeric/Likert columns
  });
  const [durationRanges, setDurationRanges] = useState({});
  
  // ConfigureDetectors required state
  const [respondentId, setRespondentId] = useState(null);
  const [groupByVariable, setGroupByVariable] = useState(null);
  const [isLoadingChecks, setIsLoadingChecks] = useState(false);
  const [checkError, setCheckError] = useState(null);
  const [typeValidationIssues, setTypeValidationIssues] = useState({});
  const [isExpandedDQC, setIsExpandedDQC] = useState(true);
  
  // Check results and column data
  const [checkResults, setCheckResults] = useState({});
  const [columnTypes, setColumnTypes] = useState({});
  // Always-defined view of column_types for downstream consumers (AI Assessment,
  // Detective Dashboard, Cleaning Studio, etc). The dedicated columnTypes state
  // can briefly drift out of sync with qualityChecksData (e.g. transient state
  // restore order, or before Configure Detectors has mounted to repopulate it),
  // so fall back to the data file's persisted column_types — which is the
  // canonical schema returned by the backend on every load and state restore.
  const effectiveColumnTypes = useMemo(() => {
    if (columnTypes && Object.keys(columnTypes).length > 0) return columnTypes;
    return qualityChecksData?.column_types || {};
  }, [columnTypes, qualityChecksData]);

  // Detect whether the loaded primary data has any audio file columns.
  // Drives visibility of the Audio Audit nav chip — same pattern as
  // backcheckDataLoaded gates the back-check tools. Uses the same heuristic
  // detector that the Audio Audit workspace uses internally, so what the nav
  // shows and what the workspace finds always match.
  const hasAudioColumns = useMemo(() => {
    // Chip stays visible in the web build too (discoverability): clicking
    // it there shows the get-the-desktop-app explainer in the route case.
    if (!qualityChecksData?.columns || !qualityChecksData?.data) return false;
    const colNames = qualityChecksData.columns
      .map((c) => (typeof c === 'string' ? c : (c?.name || c?.column || '')))
      .filter(Boolean);
    try {
      const detected = detectAudioColumns(qualityChecksData.data, colNames);
      return detected.length > 0;
    } catch (e) {
      return false;
    }
  }, [qualityChecksData]);

  // Self-healing sync: if qualityChecksData has column_types but the dedicated
  // state is empty (legacy projects with corrupted checksState, or transient
  // post-restore drift), copy the values up so future auto-saves persist a
  // good value to checksState instead of perpetually writing {} back to DB.
  useEffect(() => {
    const dataColTypes = qualityChecksData?.column_types;
    const stateEmpty = !columnTypes || Object.keys(columnTypes).length === 0;
    if (stateEmpty && dataColTypes && Object.keys(dataColTypes).length > 0) {
      setColumnTypes(dataColTypes);
    }
  }, [qualityChecksData, columnTypes]);

  const [dataCollectorId, setDataCollectorId] = useState(null);
  const [interviewDateId, setInterviewDateId] = useState(null);
  
  // Project state tracking
  const [projectState, setProjectState] = useState({
    dataLoaded: false,
    checksCompleted: [],
    mlAssessmentCompleted: false,
    cleaningCompleted: false,
    reportGenerated: false,
    timestamp: null
  });
  
  // ============================================
  // MODAL STATE
  // ============================================
  
  const [activeModal, setActiveModal] = useState(() => {
    try { return localStorage.getItem('dq_active_modal') || null; } catch { return null; }
  });
  const [modalParams, setModalParams] = useState({});
  const [lastVisitedModal, setLastVisitedModal] = useState(null);
  
  // Invite acceptance state
  const [pendingInvite, setPendingInvite] = useState(null); // { token, org_name, role, ... }
  const [inviteProcessing, setInviteProcessing] = useState(false);
  
  // Debounce ref for save operations - prevents cascading saves from blocking UI
  const saveTimerRef = useRef(null);

  // ──────────────────────────────────────────────────────────────────────────
  // SAVE QUEUES
  // saveQueueRef: full /state endpoint (big payload, low frequency)
  // lastViewQueueRef: tiny /last-view endpoint (small payload, every navigation)
  // Each queue holds at most one in-flight request and one coalesced pending
  // request — newer pending updates merge into older ones (full state) or
  // overwrite (last-view, only the latest view matters).
  // ──────────────────────────────────────────────────────────────────────────
  const saveQueueRef = useRef({ inFlight: null, queued: null });
  const lastViewQueueRef = useRef({ inFlight: null, queued: null });

  // Live snapshot of all state needed by saveProjectState. Refreshed on every
  // render — saveProjectState reads from this instead of closures, so saves
  // fired from setTimeout / post-restore effects never see stale null values.
  // (This is the actual bug behind the dropped lastView writes — useCallback
  // had captured first-render state where currentProject and userInfo were null.)
  const liveStateRef = useRef({});

  // Live ref for the restoration flag — auto-save guards inside async callbacks
  // need the current value, not a stale closure value from when scheduled.
  const isRestoringStateRef = useRef(false);
  useEffect(() => { isRestoringStateRef.current = isRestoringState; }, [isRestoringState]);

  // True while we have a persisted project but haven't finished restoring state yet
  const isAwaitingRestore = !!currentProject && !isRestoringState && !activeModal && !data;

  // ── Persist currentProject to localStorage ──
  useEffect(() => {
    try {
      if (currentProject) localStorage.setItem('dq_current_project', JSON.stringify(currentProject));
      else localStorage.removeItem('dq_current_project');
    } catch {}
  }, [currentProject]);

  useEffect(() => {
    try {
      if (activeModal) localStorage.setItem('dq_active_modal', activeModal);
      else localStorage.removeItem('dq_active_modal');
    } catch {}
  }, [activeModal]);
  
  // ============================================
  // LIVE STATE SNAPSHOT (refreshed every render)
  // ============================================
  // Single source of truth for saveProjectState. Updated synchronously during
  // render so any async callback (setTimeout, post-restore effects, queue
  // drain) reads the latest values. Adding new state slices to saves means
  // adding them here only — saveProjectState reads from refs blind.
  liveStateRef.current = {
    currentProject, userInfo, activeModal,
    selectedChecks, selectedVariables, dataCollectorId, respondentId,
    interviewDateId, groupByVariable, checkResults, columnTypes,
    typeValidationIssues, isExpandedDQC, activeQualityTab,
    textClusteringSettings, sentinelSettings, gatedFieldsSettings,
    probeDepthSettings, straightLiningSettings, durationRanges,
    backcheckConfig, isLoadingBackcheck, backcheckError, backcheckResults,
    isExpandedBackcheck, cleaningComplete, cleanedCheckResults, cleanedData,
    cleaningStudioState, reportGenerated, xlsformData, projectState,
    backcheckQualityData,
  };

  // ============================================
  // DERIVED STATE
  // ============================================
  
  const dataLoaded = !!qualityChecksData;
  const qualityChecksRun = checkResults && Object.keys(checkResults).length > 0;
  const mlAssessmentRun = false;  // AI Assessment retired — always false
  const backcheckDataLoaded = !!backcheckData;
  const isBackcheckConfigured = () => {
    return backcheckConfig.backcheckDataCollectorId && 
           backcheckConfig.backcheckInterviewDateId && 
           (backcheckConfig.type1Variables.length > 0 || 
            backcheckConfig.type2Variables.length > 0 || 
            backcheckConfig.type3Variables.length > 0);
  };
  
  // ============================================
  // MODAL HANDLERS
  // ============================================
  
  const handleOpenModal = (modalId, params = {}) => {
    setActiveModal(modalId);
    setModalParams(params);
  };
  
  const handleCloseModal = () => {
    // Track the last visited modal before closing
    if (activeModal) {
      setLastVisitedModal(activeModal);
    }
    setActiveModal(null);
    setModalParams({});
  };

  // Select-only handler: highlights menu item + updates workspace preview
  // WITHOUT opening the ContentModal. User explicitly opens the modal via
  // the "resume work" CTA (which routes through handlePreviewClick).
  const handleSelectModal = (modalId) => {
    if (!modalId) return;
    setLastVisitedModal(modalId);
    // Don't touch activeModal — keeps the modal closed and lets the
    // workspace preview update via generatePreviewContent().
  };

  // Valid modal IDs that can be saved as lastView
  const validModalIds = [
    'projects', 'data-source', 'primary-survey', 'xlsform', 'backcheck-dataset',
    'datepreview', 'data-summary', 'correlation-matrix', 'collector-dashboard', 
    'quality-checks', 'dashboard', 'configure-back-checks', 'backcheckdashboard',
    'cleaning-studio', 'cleaned-review',
    'data-export', 'generate-report',
    'audio-audit',
  ];
  
  const handleNavigate = (viewId, params = {}) => {
    // Navigate FIRST — UI updates immediately
    handleOpenModal(viewId, params);

    // Persist navigation state in two paths:
    //   1. Tiny /last-view endpoint — fires immediately, never blocked by big
    //      saves, ~50 byte payload. This is the canonical record of where the
    //      user is. Even if path 2 fails or is slow, restore will land here.
    //   2. Full /state endpoint via debouncedSave — coalesces with other state
    //      changes happening at the same time (selectedChecks, etc).
    if (currentProject && !isRestoringState && validModalIds.includes(viewId)) {
      saveLastView(viewId);
      debouncedSave({ lastView: viewId });
    } else if (!validModalIds.includes(viewId)) {
      console.warn('handleNavigate: Invalid viewId, not saving as lastView:', viewId);
    }
  };

  // Live ref for saveProjectState — the function is recreated on every render
  // and is declared further down the file. The ref is initialized null here
  // and synced to the latest function after that declaration runs (see the
  // assignment immediately after `const saveProjectState = …` below).
  // debouncedSave's setTimeout reads through this ref so it always invokes
  // the latest definition. This is what fixes the original
  // "saveProjectState: SKIPPED - hasProject: false" bug — useCallback([])
  // was previously capturing the FIRST render's saveProjectState whose
  // closure had null currentProject and userInfo from before login.
  const saveProjectStateRef = useRef(null);

  // Debounced save — coalesces multiple rapid saves into one, deferred off main thread
  const debouncedSave = useCallback((stateUpdate = {}) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      const fn = saveProjectStateRef.current;
      if (fn) fn(stateUpdate);
    }, 500);
  }, []);
  
  const handleBackToModeSelector = () => {
    setShowModeSelector(true);
    handleCloseModal();
  };
  
  // ============================================
  // AUTH & INITIALIZATION
  // ============================================
  
  useEffect(() => {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        setUserInfo(parsedUserInfo);
      } catch (error) {
        console.error('Error parsing stored user info:', error);
        navigate('/login');
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);
  
  // ============================================
  // INVITE TOKEN DETECTION
  // ============================================
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('invite');
    if (!inviteToken || !userInfo?.email) return;

    // Clean the URL immediately so refreshes don't re-trigger
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    // Validate the token against the API
    const validateInvite = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/team/invitations/pending`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'user_email': userInfo.email,
            'Content-Type': 'application/json',
          },
        });
        if (!res.ok) return;
        const data = await res.json();
        const match = data.invitations?.find(inv => inv.id || true);
        // We have the token, show the accept banner with token info
        setPendingInvite({
          token: inviteToken,
          invitations: data.invitations || [],
        });
      } catch (e) {
        console.error('Failed to validate invite:', e);
      }
    };
    validateInvite();
  }, [userInfo]);

  const handleAcceptInvite = async (token) => {
    setInviteProcessing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/invitations/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (res.ok) {
        setPendingInvite(null);
        // Navigate to projects which has the Team tab
        setActiveModal('projects');
      } else {
        alert(data.detail || 'Failed to accept invitation');
      }
    } catch (e) {
      console.error('Accept invite error:', e);
      alert('Something went wrong. Please try again.');
    } finally {
      setInviteProcessing(false);
    }
  };

  const handleDismissInvite = () => {
    setPendingInvite(null);
  };

  const handleLogout = async () => {
    try {
      // Flush any pending saves BEFORE tearing down React tree — otherwise
      // the last navigation save (e.g. user clicked Dashboard then Logout)
      // gets cancelled mid-fetch when the component unmounts.
      await flushPendingSaves();

      localStorage.removeItem('userInfo');
      localStorage.removeItem('lastProjectId');
      localStorage.removeItem('dq_current_project');
      localStorage.removeItem('dq_active_modal');
      setUserInfo(null);
      setCurrentProject(null);
      setActiveModal(null);
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      navigate('/login'); // navigate anyway — don't trap the user
    }
  };
  
  // ============================================
  // CLEAR PREVIOUS STATE
  // ============================================
  
  const clearPreviousState = () => {
    // Reset all data-related state when switching projects
    setData(null);
    setQualityChecksData(null);
    setBackcheckData(null);
    setBackcheckQualityData(null);
    setSelectedChecks([]);
    setSelectedVariables({
      duplicates: [],
      missingness: [],
      outliers: [],
      duration: [],
      survey_flow: []
    });
    setDataCollectorId('');
    setRespondentId('');
    setInterviewDateId('');
    setGroupByVariable('');
    setCheckResults({});
    setIsLoadingChecks(false);
    setCheckError(null);
    setColumnTypes({});
    setTypeValidationIssues({});
    setIsExpandedDQC(true);
    setActiveQualityTab(null);
    
    // Reset cleaning state
    setCleaningComplete(false);
    setCleanedCheckResults({});
    setCleanedData(null);
    
    // Reset CleaningStudio UI state
    setCleaningStudioState({
      keyVariables: { respondentId: '', collectorId: '', interviewDate: '', surveyUniqueKey: '' },
      selectedIssues: [],
      scriptContent: '',
      consoleOutput: [],
      versions: [],
      currentVersion: null
    });
    
    // Reset report state
    setReportGenerated(false);
    
    // Reset backcheck state
    setBackcheckConfig({
      primaryRespondentId: '',
      backcheckRespondentId: '',
      backcheckDataCollectorId: '',
      backcheckInterviewDateId: '',
      type1Variables: [],
      type2Variables: [],
      type3Variables: []
    });
    setIsLoadingBackcheck(false);
    setBackcheckError(null);
    setBackcheckResults(null);
    setIsExpandedBackcheck(true);

    // Reset XLSForm state
    setXLSFormData(null);
    setXLSFormMatchingResult(null);
    setXLSFormFileName(null);
    
    // Reset project state tracking
    setProjectState({
      dataLoaded: false,
      checksCompleted: [],
      mlAssessmentCompleted: false,
      cleaningCompleted: false,
      reportGenerated: false,
      timestamp: null
    });
  };
  
  // ============================================
  // SAVE PROJECT STATE
  // ============================================
  
  // ============================================
  // SAVE PROJECT STATE — queued, non-blocking, ref-backed
  // ============================================
  // Architecture:
  //   saveProjectState(stateUpdate) coalesces into a queue and returns a
  //   promise that resolves when the save round-trip completes. Concurrent
  //   callers don't block each other and don't drop work — they merge into a
  //   single pending payload (newer keys override older ones) and run as soon
  //   as the in-flight request resolves.
  //
  //   _doSaveOnce reads ALL state from liveStateRef.current — no closures —
  //   so setTimeout / post-restore callers always see fresh values.
  // ============================================

  const _doSaveOnce = async (stateUpdate) => {
    const live = liveStateRef.current;
    if (!live.currentProject || !live.userInfo?.email) {
      console.log('saveProjectState: SKIPPED - no project or user', {
        hasProject: !!live.currentProject,
        projectId: live.currentProject?.id,
        hasEmail: !!live.userInfo?.email
      });
      return;
    }

    // Skip auto-saves during state restoration, but allow explicit saves
    const isExplicitSave = Object.keys(stateUpdate).length > 0;
    if (isRestoringStateRef.current && !isExplicitSave) {
      console.log('Skipping auto-save during state restoration');
      return;
    }

    console.log('saveProjectState: SAVING to project', live.currentProject.id);

    try {
      const stateToSave = {
        projectId: live.currentProject.id,
        timestamp: new Date().toISOString(),
        lastView: live.activeModal,
        // PERFORMANCE: data, qualityChecksData, backcheckData removed from save payload.
        // Raw data lives in data_files table — no need to round-trip through state saves.
        backcheckQualityData: live.backcheckQualityData,
        checksState: {
          selectedChecks: live.selectedChecks,
          selectedVariables: live.selectedVariables,
          dataCollectorId: live.dataCollectorId,
          respondentId: live.respondentId,
          interviewDateId: live.interviewDateId,
          groupByVariable: live.groupByVariable,
          checkResults: live.checkResults,
          columnTypes: live.columnTypes,
          typeValidationIssues: live.typeValidationIssues,
          isExpandedDQC: live.isExpandedDQC,
          activeQualityTab: live.activeQualityTab,
          textClusteringSettings: live.textClusteringSettings,
          sentinelSettings: live.sentinelSettings,
          gatedFieldsSettings: live.gatedFieldsSettings,
          probeDepthSettings: live.probeDepthSettings,
          straightLiningSettings: live.straightLiningSettings,
          durationRanges: live.durationRanges
        },
        // Save backcheck state
        backcheckState: {
          backcheckConfig: live.backcheckConfig,
          isLoadingBackcheck: live.isLoadingBackcheck,
          backcheckError: live.backcheckError,
          backcheckResults: live.backcheckResults,
          isExpandedBackcheck: live.isExpandedBackcheck
        },
        // Save cleaning state
        cleaningState: {
          cleaningComplete: live.cleaningComplete,
          cleanedCheckResults: live.cleanedCheckResults,
          cleanedData: live.cleanedData,
          cleaningStudioState: live.cleaningStudioState
        },
        // Save report state
        reportState: {
          reportGenerated: live.reportGenerated
        },
        // Save XLSForm state
        xlsformState: live.xlsformData ? {
          parseResult: live.xlsformData.parseResult,
          matchingResult: live.xlsformData.matchingResult,
          fileName: live.xlsformData.fileName
        } : null,
        projectState: live.projectState,
        ...stateUpdate
      };

      const response = await fetch(`${API_BASE_URL}/api/projects/${live.currentProject.id}/state`, {
        method: 'PUT',
        headers: {
          'user_email': live.userInfo.email,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(stateToSave)
      });

      if (!response.ok) {
        console.error('Failed to save project state', response.status);
      } else {
        console.log('Project state saved successfully');
      }
    } catch (error) {
      console.error('Error saving project state:', error);
    }
  };

  const saveProjectState = (stateUpdate = {}) => {
    // Coalesce into queued update — newer keys override older ones
    saveQueueRef.current.queued = {
      ...(saveQueueRef.current.queued || {}),
      ...stateUpdate
    };

    // If a save is already in flight, return its promise — the queued update
    // will be picked up when the loop loops.
    if (saveQueueRef.current.inFlight) return saveQueueRef.current.inFlight;

    const drain = async () => {
      while (saveQueueRef.current.queued) {
        const update = saveQueueRef.current.queued;
        saveQueueRef.current.queued = null;
        try {
          await _doSaveOnce(update);
        } catch (e) {
          console.error('Save error in queue drain:', e);
        }
      }
      saveQueueRef.current.inFlight = null;
    };

    saveQueueRef.current.inFlight = drain();
    return saveQueueRef.current.inFlight;
  };

  // Sync the ref so debouncedSave's setTimeout always invokes the latest
  // definition (the declaration above runs every render — this line
  // immediately threads it through the ref that debouncedSave reads).
  saveProjectStateRef.current = saveProjectState;

  // Tiny dedicated last-view writer — separate queue, separate endpoint.
  // Never contends with the big /state endpoint. Tiny payload (~50 bytes),
  // fires on every navigation, can't be dropped by a stalled big save.
  const saveLastView = (viewId) => {
    if (!viewId) return;
    lastViewQueueRef.current.queued = viewId; // overwrite — only latest matters

    if (lastViewQueueRef.current.inFlight) return lastViewQueueRef.current.inFlight;

    const drain = async () => {
      while (lastViewQueueRef.current.queued) {
        const view = lastViewQueueRef.current.queued;
        lastViewQueueRef.current.queued = null;
        const live = liveStateRef.current;
        if (!live.currentProject || !live.userInfo?.email) continue;
        try {
          await fetch(`${API_BASE_URL}/api/projects/${live.currentProject.id}/last-view`, {
            method: 'PUT',
            headers: {
              'user_email': live.userInfo.email,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ view })
          });
        } catch (e) {
          console.error('saveLastView error:', e);
        }
      }
      lastViewQueueRef.current.inFlight = null;
    };

    lastViewQueueRef.current.inFlight = drain();
    return lastViewQueueRef.current.inFlight;
  };

  // Force any pending debounced save to fire NOW and wait for both queues to
  // fully drain. Called before logout / page unload so navigation writes
  // aren't lost when the React tree tears down.
  const flushPendingSaves = async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      saveProjectState({});
    }
    try { if (saveQueueRef.current.inFlight)     await saveQueueRef.current.inFlight; } catch {}
    try { if (lastViewQueueRef.current.inFlight) await lastViewQueueRef.current.inFlight; } catch {}
  };
  
  // ============================================
  // LOAD PROJECT STATE (when project changes)
  // ============================================
  
  useEffect(() => {
    const loadProjectState = async () => {
      if (currentProject && userInfo?.email) {
        // Set restoring flag to prevent auto-saves during restoration
        setIsRestoringState(true);
        
        try {
          console.log(`Loading state for project: ${currentProject.id}`);

          // Local projects: start the engine and switch the API base BEFORE
          // fetching state, so auto-resume works for them too.
          if (currentProject.storage_mode === 'local') {
            try {
              await activateProjectStorage(currentProject);
            } catch (e) {
              console.error('Local engine failed to start for auto-resume:', e);
            }
          }

          const response = await fetch(`${API_BASE_URL}/api/projects/${currentProject.id}/state`, {
            headers: {
              'user_email': userInfo.email,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const savedState = await response.json();
            console.log('Loaded project state:', savedState);
            
            // Restore primary data if available
            if (savedState.data) {
              console.log('Restoring primary data state');
              setData(savedState.data);
              // qualityChecksData = metadata + preview (no full_data needed — checks use v2/DB)
              setQualityChecksData(savedState.qualityChecksData || savedState.data);
              setColumnTypes(savedState.columnTypes || {});
            }
            
            // Restore backcheck data if available
            if (savedState.backcheckData) {
              console.log('Restoring backcheck data state');
              setBackcheckData(savedState.backcheckData);
              setBackcheckQualityData(savedState.backcheckQualityData);
            }

            // Restore backcheck config state if available
            if (savedState.backcheckState) {
              console.log('Restoring backcheck configuration state');
              setBackcheckConfig(savedState.backcheckState.backcheckConfig || {
                primaryRespondentId: '',
                backcheckRespondentId: '',
                backcheckDataCollectorId: '',
                backcheckInterviewDateId: '',
                type1Variables: [],
                type2Variables: [],
                type3Variables: []
              });
              
              if (savedState.backcheckState.backcheckResults) {
                setBackcheckResults(savedState.backcheckState.backcheckResults);
              }
              
              setIsExpandedBackcheck(savedState.backcheckState.isExpandedBackcheck !== undefined 
                ? savedState.backcheckState.isExpandedBackcheck : true);
            }
            
            // Restore checks state if available
            if (savedState.checksState) {
              console.log('Restoring checks state');
              setSelectedChecks(savedState.checksState.selectedChecks || []);
              setSelectedVariables(savedState.checksState.selectedVariables || {
                duplicates: [],
                missingness: [],
                outliers: [],
                duration: [],
                survey_flow: []
              });
              setDataCollectorId(savedState.checksState.dataCollectorId || '');
              setRespondentId(savedState.checksState.respondentId || '');
              setInterviewDateId(savedState.checksState.interviewDateId || '');
              setGroupByVariable(savedState.checksState.groupByVariable || '');
              setCheckResults(savedState.checksState.checkResults || {});
              // Only restore checksState.columnTypes if it's actually populated.
              // An empty {} here will clobber the good top-level columnTypes set
              // earlier from primary_data.column_types — which is what was
              // breaking AI Assessment when an old auto-save captured empty
              // columnTypes back when preview's setColumnTypes was a noop.
              const savedChecksColTypes = savedState.checksState.columnTypes;
              if (savedChecksColTypes && Object.keys(savedChecksColTypes).length > 0) {
                setColumnTypes(savedChecksColTypes);
              }
              setTypeValidationIssues(savedState.checksState.typeValidationIssues || {});
              setIsExpandedDQC(savedState.checksState.isExpandedDQC !== undefined 
                ? savedState.checksState.isExpandedDQC : true);
              
              if (savedState.checksState.activeQualityTab && savedState.checksState.activeQualityTab !== 'heatmap') {
                setActiveQualityTab(savedState.checksState.activeQualityTab);
              }
              
              if (savedState.checksState.textClusteringSettings) {
                setTextClusteringSettings(savedState.checksState.textClusteringSettings);
              }

              if (savedState.checksState.sentinelSettings) {
                setSentinelSettings(savedState.checksState.sentinelSettings);
              }

              if (savedState.checksState.gatedFieldsSettings) {
                setGatedFieldsSettings(savedState.checksState.gatedFieldsSettings);
              }

              if (savedState.checksState.probeDepthSettings) {
                setProbeDepthSettings(savedState.checksState.probeDepthSettings);
              }

              if (savedState.checksState.straightLiningSettings) {
                setStraightLiningSettings(savedState.checksState.straightLiningSettings);
              }

              if (savedState.checksState.durationRanges) {
                setDurationRanges(savedState.checksState.durationRanges);
              }
            }

            // Restore cleaning state if available
            if (savedState.cleaningState) {
              console.log('Restoring cleaning state');
              setCleaningComplete(savedState.cleaningState.cleaningComplete || false);
              setCleanedCheckResults(savedState.cleaningState.cleanedCheckResults || {});
              if (savedState.cleaningState.cleanedData) {
                setCleanedData(savedState.cleaningState.cleanedData);
              }
              if (savedState.cleaningState.cleaningStudioState) {
                setCleaningStudioState(savedState.cleaningState.cleaningStudioState);
              }
            }

            // Restore report state if available
            if (savedState.reportState) {
              console.log('Restoring report state');
              setReportGenerated(savedState.reportState.reportGenerated || false);
            }

            // Restore project state tracking
            if (savedState.projectState) {
              setProjectState(savedState.projectState);
            }

            // Restore XLSForm state if available
            if (savedState.xlsformState) {
              console.log('Restoring XLSForm state');
              setXLSFormData(savedState.xlsformState);
              setXLSFormMatchingResult(savedState.xlsformState.matchingResult);
              setXLSFormFileName(savedState.xlsformState.fileName);
            }

            // SMART VIEW RESTORATION: Navigate to last view user was on,
            // walking a fallback ladder if the intended view's requirements
            // aren't met (instead of dropping straight back to DataLoader).
            const lastView = savedState.lastView;
            console.log('Attempting to restore lastView:', lastView);

            if (lastView) {
              // Define valid modal IDs and their requirements
              const validModalIds = [
                'projects', 'data-source', 'primary-survey', 'xlsform', 'backcheck-dataset',
                'datepreview', 'data-summary', 'correlation-matrix', 'collector-dashboard',
                'quality-checks', 'dashboard', 'configure-back-checks', 'backcheckdashboard',
                'cleaning-studio', 'cleaned-review',
                'data-export', 'generate-report'
              ];

              // Check if lastView is a valid modal ID (not a parent node like 'setup', 'explore', etc.)
              if (!validModalIds.includes(lastView)) {
                console.log('Invalid lastView (parent node or unknown):', lastView, '- navigating to primary-survey');
                setActiveModal('primary-survey');
              } else {
                const viewRequirements = {
                  'datepreview': () => savedState.data || savedState.backcheckData,
                  'data-summary': () => savedState.data,
                  'correlation-matrix': () => savedState.data,
                  'collector-dashboard': () => savedState.qualityChecksData,
                  'quality-checks': () => savedState.data,
                  'dashboard': () => savedState.checksState?.checkResults && Object.keys(savedState.checksState.checkResults).length > 0,
                  'configure-back-checks': () => savedState.backcheckData,
                  'backcheckdashboard': () => savedState.backcheckState?.backcheckResults,
                  'cleaning-studio': () => savedState.data,
                  'cleaned-review': () => savedState.cleaningState?.cleanedData,
                  'data-export': () => savedState.cleaningState?.cleanedData,
                  'generate-report': () => savedState.data,
                  'projects': () => true,
                  'data-source': () => true,
                  'primary-survey': () => true,
                  'xlsform': () => true,
                  'backcheck-dataset': () => true,
                };

                // Fallback ladder: ordered list of acceptable views per intended view.
                // Walk down until we find one whose requirements are met. This prevents
                // a user who left off on 'dashboard' (but whose checkResults didn't
                // round-trip) from being dropped all the way back to DataLoader —
                // they'll land on 'quality-checks' instead, where data is still
                // available and they can re-run with a single click.
                const fallbackLadder = {
                  'dashboard':            ['dashboard', 'quality-checks', 'datepreview', 'primary-survey'],
                  'quality-checks':       ['quality-checks', 'datepreview', 'primary-survey'],
                  'cleaning-studio':      ['cleaning-studio', 'dashboard', 'quality-checks', 'datepreview', 'primary-survey'],
                  'cleaned-review':       ['cleaned-review', 'cleaning-studio', 'dashboard', 'datepreview', 'primary-survey'],
                  'data-export':          ['data-export', 'cleaned-review', 'cleaning-studio', 'datepreview', 'primary-survey'],
                  'generate-report':      ['generate-report', 'dashboard', 'datepreview', 'primary-survey'],
                  'backcheckdashboard':   ['backcheckdashboard', 'configure-back-checks', 'datepreview', 'primary-survey'],
                  'configure-back-checks':['configure-back-checks', 'datepreview', 'primary-survey'],
                  'data-summary':         ['data-summary', 'datepreview', 'primary-survey'],
                  'correlation-matrix':   ['correlation-matrix', 'datepreview', 'primary-survey'],
                  'collector-dashboard':  ['collector-dashboard', 'datepreview', 'primary-survey'],
                  'datepreview':          ['datepreview', 'primary-survey'],
                  // Always-available views fall back to themselves
                  'projects':             ['projects'],
                  'data-source':          ['data-source'],
                  'primary-survey':       ['primary-survey'],
                  'xlsform':              ['xlsform'],
                  'backcheck-dataset':    ['backcheck-dataset'],
                };

                const candidates = fallbackLadder[lastView] || [lastView, 'primary-survey'];
                let restored = null;
                for (const candidate of candidates) {
                  const check = viewRequirements[candidate];
                  if (!check || check()) {
                    restored = candidate;
                    break;
                  }
                }
                if (!restored) restored = 'primary-survey';

                if (restored === lastView) {
                  console.log('Restoring to lastView:', restored);
                } else {
                  console.log(`Restoring to fallback view: ${restored} (intended: ${lastView}, requirements not met)`);
                }
                setActiveModal(restored);
              }
            } else {
              // No lastView saved - this is likely a new project, go to primary-survey
              console.log('No lastView saved, navigating to primary-survey');
              setActiveModal('primary-survey');
            }

          } else if (response.status === 404 || response.status === 403) {
            // Stale project ID — project was deleted, or belongs to a different
            // account (common after re-login on Tauri where localStorage persists).
            // Clear the stale ID so the user lands on the welcome screen instead
            // of hanging on the "Restoring your workspace…" overlay.
            console.warn(
              `Project ${currentProject.id} returned ${response.status} — clearing stale project reference.`
            );
            localStorage.removeItem('dq_current_project');
            localStorage.removeItem('dq_active_modal');
            setCurrentProject(null);
            setActiveModal(null);
            clearPreviousState();
          } else {
            console.log('No saved state found for project');
            clearPreviousState();
          }
        } catch (error) {
          console.error('Error loading project state:', error);
          // Network failure or backend unreachable. Don't wipe the project ID
          // (user might be offline briefly) — just clear derived state and bail
          // out of restoration so the overlay dismisses.
          clearPreviousState();
        } finally {
          // Clear restoring flag after a short delay to allow state to settle
          setTimeout(() => {
            setIsRestoringState(false);
            console.log('State restoration complete, auto-save enabled');
          }, 1000);
        }
      }
    };

    loadProjectState();
  }, [currentProject?.id, userInfo?.email]);
  
  // ============================================
  // AUTO-REFRESH FROM API (using DataService)
  // ============================================
  
  useEffect(() => {
    const autoRefreshFromAPI = async () => {
      if (!currentProject?.id || !userInfo?.email || isRestoringState) return;
      
      try {
        // Check if project has saved API config for primary data
        const primaryConfig = await DataService.getAPIConfig(currentProject.id, 'primary');
        
        if (primaryConfig && primaryConfig.has_config && primaryConfig.config.auto_refresh && !autoRefreshFailedRef.current.has(currentProject.id)) {
          console.log('Auto-refreshing primary data from saved API config...');
          setIsAutoRefreshing(true);
          setAutoRefreshError(null);
          
          try {
            const refreshedData = await DataService.refreshAPIData(currentProject.id, 'primary');
            console.log('Auto-refresh successful:', refreshedData.total_rows, 'rows');
            
            // Update data state
            setData({
              data: refreshedData.data,
              total_rows: refreshedData.total_rows,
              total_variables: refreshedData.total_variables,
              columns: refreshedData.columns
            });
            
            setQualityChecksData({
              data: refreshedData.data,
              column_types: refreshedData.column_types,
              columns: refreshedData.columns,
              total_rows: refreshedData.total_rows,
              total_variables: refreshedData.total_variables
            });
            
            if (refreshedData.column_types) {
              setColumnTypes(refreshedData.column_types);
            }
            
          } catch (refreshError) {
            // A dead source returns an HTML error page — turn that into a
            // human sentence, remember the failure, and stop retrying this
            // session so navigation stays snappy on cached data.
            const friendly = /Server Not Found|<!DOCTYPE|<html/i.test(refreshError.message || '')
              ? 'The connected data source is no longer reachable — showing cached data'
              : refreshError.message;
            console.warn('Auto-refresh failed, using cached data:', friendly);
            setAutoRefreshError(friendly);
            autoRefreshFailedRef.current.add(currentProject.id);
            // Don't clear data - keep using cached data from database
          }
        }
        
        // Similarly check for backcheck data
        const backcheckAPIConfig = await DataService.getAPIConfig(currentProject.id, 'backcheck');
        
        if (backcheckAPIConfig && backcheckAPIConfig.has_config && backcheckAPIConfig.config.auto_refresh && !autoRefreshFailedRef.current.has(currentProject.id)) {
          console.log('Auto-refreshing backcheck data from saved API config...');
          
          try {
            const refreshedBackcheck = await DataService.refreshAPIData(currentProject.id, 'backcheck');
            console.log('Backcheck auto-refresh successful:', refreshedBackcheck.total_rows, 'rows');
            
            setBackcheckData({
              data: refreshedBackcheck.data,
              total_rows: refreshedBackcheck.total_rows,
              total_variables: refreshedBackcheck.total_variables,
              columns: refreshedBackcheck.columns
            });
            
            setBackcheckQualityData(refreshedBackcheck);
            
          } catch (backcheckError) {
            console.warn('Backcheck auto-refresh failed:', backcheckError.message);
            autoRefreshFailedRef.current.add(currentProject.id);
          }
        }
        
      } catch (error) {
        console.log('No API config to auto-refresh:', error.message);
      } finally {
        setIsAutoRefreshing(false);
      }
    };
    
    // Delay auto-refresh to allow state restoration to complete first
    const timer = setTimeout(() => {
      autoRefreshFromAPI();
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [currentProject?.id, userInfo?.email, isRestoringState]);
  
  // ============================================
  // PROJECT SELECTION HANDLER (matches App.js)
  // ============================================
  
  const handleProjectSelect = (project) => {
    console.log('handleProjectSelect called with:', project?.name || 'null');
    
    if (project) {
      const previousProjectId = currentProject?.id;
      
      // Re-selecting the same project — just close the projects modal
      if (previousProjectId === project.id) {
        setActiveModal(null);
        return;
      }
      
      // Switching to a different project — clear previous state
      if (previousProjectId) {
        console.log(`Switching projects: ${previousProjectId} -> ${project.id}`);
        clearPreviousState();
      }

      // Update last accessed time
      const updatedProject = {
        ...project,
        lastAccessed: new Date().toISOString()
      };
      setCurrentProject(updatedProject);
      
      // Update project in backend
      updateProjectLastAccessed(updatedProject);
      
      // Close the projects modal — state restoration effect will navigate
      // to the correct lastView (or primary-survey for new projects)
      setActiveModal(null);
    } else {
      setCurrentProject(null);
      clearPreviousState();
    }
  };
  
  const updateProjectLastAccessed = async (project) => {
    try {
      await fetch(`${API_BASE_URL}/api/projects/${project.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'user_email': userInfo?.email
        },
        body: JSON.stringify({
          lastAccessed: project.lastAccessed
        })
      });
    } catch (error) {
      console.error('Error updating project last accessed:', error);
    }
  };
  
  // ============================================
  // AUTO-SAVE USEEFFECTS (match App.js behavior)
  // ============================================
  
  // Save state when check results change
  useEffect(() => {
    if (currentProject && Object.keys(checkResults).length > 0 && !isRestoringState) {
      const checksCompleted = Object.keys(checkResults);
      setProjectState(prev => ({
        ...prev,
        checksCompleted,
        timestamp: new Date().toISOString()
      }));

      debouncedSave();
    }
  }, [checkResults, dataCollectorId, respondentId, interviewDateId, currentProject, isRestoringState]);

  // Save state when backcheck results change
  useEffect(() => {
    if (currentProject && backcheckResults && !isRestoringState) {
      console.log('Backcheck results changed, auto-saving...');
      debouncedSave();
    }
  }, [backcheckResults, currentProject, isRestoringState]);

  // Save state when active quality tab changes
  useEffect(() => {
    if (currentProject && activeQualityTab && !isRestoringState) {
      console.log('Active quality tab changed, auto-saving...');
      debouncedSave();
    }
  }, [activeQualityTab, currentProject, isRestoringState]);
  
  // Save state when CleaningStudio state changes
  useEffect(() => {
    if (currentProject && cleaningStudioState?.savedAt && !isRestoringState) {
      console.log('CleaningStudio state changed, auto-saving...');
      debouncedSave();
    }
  }, [cleaningStudioState, currentProject, isRestoringState]);

  // Save state when XLSForm data changes
  useEffect(() => {
    if (currentProject && xlsformData && !isRestoringState) {
      console.log('XLSForm data changed, auto-saving...');
      debouncedSave();
    }
  }, [xlsformData, currentProject, isRestoringState]);
  
  // ============================================
  // DATA HANDLERS
  // ============================================
  
  const handleDataLoaded = async (loadedData) => {
    console.log('Data loaded:', loadedData);
    
    // CRITICAL: Clear any residual state from previous data loads
    // This prevents stale data (like old variables in dropdowns) from persisting
    setSelectedChecks([]);
    setSelectedVariables({
      duplicates: [],
      missingness: [],
      outliers: [],
      duration: [],
      survey_flow: [],
      geolocation: [],
      text_clustering: []
    });
    setCheckResults({});
    setBackcheckResults(null);
    
    // Reset CleaningStudio key variables
    setCleaningStudioState(prev => ({
      ...prev,
      keyVariables: { respondentId: '', collectorId: '', interviewDate: '', surveyUniqueKey: '' }
    }));
    
    // Set preview data
    setData({
      data: loadedData.data,
      total_rows: loadedData.total_rows,
      total_variables: loadedData.total_variables,
      columns: loadedData.columns
    });
    
    // Set full dataset for quality checks
    setQualityChecksData({
      data: loadedData.data,
      column_types: loadedData.column_types,
      columns: loadedData.columns,
      total_rows: loadedData.total_rows,
      total_variables: loadedData.total_variables,
      file_name: loadedData.file_name
    });
    
    if (loadedData.column_types && Object.keys(loadedData.column_types).length > 0) {
      setColumnTypes(loadedData.column_types);
    }
    
    if (loadedData.type_validation_issues) {
      setTypeValidationIssues(loadedData.type_validation_issues);
    }
    
    if (loadedData.data_collector_id) {
      setDataCollectorId(loadedData.data_collector_id);
    }
    
    if (loadedData.interview_date_id) {
      setInterviewDateId(loadedData.interview_date_id);
    }
    
    // Clear cleaned data when new data is loaded
    setCleanedData(null);
    setCleaningComplete(false);
    setCleanedCheckResults({});

    // Update project state
    setProjectState(prev => ({
      ...prev,
      dataLoaded: true,
      checksCompleted: [],
      mlAssessmentCompleted: false,
      timestamp: new Date().toISOString()
    }));

    // Save state after data loading
    await saveProjectState({
      lastView: 'primary-survey'
    });
  };
  
  const handleBackcheckDataLoaded = async (loadedData) => {
    console.log('Back-check data loaded:', loadedData);
    console.log('Current project when loading backcheck:', currentProject?.id || 'NO PROJECT SELECTED');
    setBackcheckData({
      data: loadedData.data,
      total_rows: loadedData.total_rows,
      total_variables: loadedData.total_variables,
      columns: loadedData.columns,
      file_name: loadedData.file_name
    });
    setBackcheckQualityData({
      data: loadedData.data,
      column_types: loadedData.column_types,
      columns: loadedData.columns,
      total_rows: loadedData.total_rows,
      total_variables: loadedData.total_variables
    });
    
    // Save state after backcheck data loading
    await saveProjectState();
  };
  
  const handleXLSFormLoaded = async (xlsformResult) => {
    if (!xlsformResult) {
      setXLSFormData(null);
      setXLSFormMatchingResult(null);
      setXLSFormFileName(null);
      await saveProjectState({ xlsformState: null });
      return;
    }
    
    const newXlsformState = {
      parseResult: xlsformResult.parseResult,
      matchingResult: xlsformResult.matchingResult,
      fileName: xlsformResult.fileName
    };
    
    setXLSFormData(newXlsformState);
    setXLSFormMatchingResult(xlsformResult.matchingResult);
    setXLSFormFileName(xlsformResult.fileName);
    
    // Save state after XLSForm loading
    await saveProjectState({ xlsformState: newXlsformState });
  };
  
  // ============================================
  // MODAL CONFIG
  // ============================================
  
  const getModalConfig = (modalId) => {
    const configs = {
      'projects': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#38B6FF',
        maxWidth: '1200px',
        hideHeader: true,
      },
      'data-source': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#10B981',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'primary-survey': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#10B981',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'xlsform': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#10B981',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'backcheck-dataset': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#10B981',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'datepreview': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#38B6FF',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'data-summary': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#38B6FF',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'correlation-matrix': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#8B5CF6',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'collector-dashboard': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#F59E0B',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'configure-back-checks': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#8B5CF6',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'backcheckdashboard': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#8B5CF6',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'quality-checks': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#8B5CF6',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'dashboard': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#38B6FF',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'cleaning-studio': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#F59E0B',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'cleaned-review': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#10B981',
        maxWidth: '95vw',
        hideHeader: true,
      },
      'data-export': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#38B6FF',
        maxWidth: '900px',
        hideHeader: true,
      },
      'generate-report': {
        title: null,
        subtitle: null,
        icon: null,
        iconColor: '#10B981',
        maxWidth: '95vw',
        hideHeader: true,
      },
    };
    
    return configs[modalId] || {
      title: modalId,
      icon: Database,
      iconColor: '#38B6FF',
      maxWidth: '95vw',
      hideHeader: false,
    };
  };
  
  // ============================================
  // RENDER MODAL CONTENT
  // Props EXACTLY match what each component expects
  // ============================================
  
  const renderViewContent = (viewId) => {
    if (!viewId) return null;
    
    switch (viewId) {
      case 'projects':
        return (
          <Projects
            onProjectSelect={handleProjectSelect}
            currentProject={currentProject}
            userEmail={userInfo?.email}
          />
        );
        
      case 'primary-survey':
      case 'xlsform':
      case 'backcheck-dataset':
      case 'data-source': {
        // CRITICAL: Require project selection before data loading
        if (!currentProject) {
          return (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              minHeight: '400px',
              textAlign: 'center',
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              padding: '2rem',
              backgroundColor: isDarkMode ? '#0D0E0E' : '#F5F5F5',
            }}>
              <FolderOpen size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <h2 style={{ fontSize: '24px', marginBottom: '16px', color: isDarkMode ? '#E5E7EB' : '#1F2937' }}>
                No Project Selected
              </h2>
              <p style={{ fontSize: '16px', marginBottom: '24px', maxWidth: '400px' }}>
                Please select or create a project before loading data. Your data and API connections will be saved to the project.
              </p>
              <button
                onClick={() => handleOpenModal('projects')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4FC3F7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2ea3e8'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4FC3F7'}
              >
                Go to Projects
              </button>
            </div>
          );
        }
        // Map modal IDs to DataLoader dataset types
        const datasetTypeMap = {
          'primary-survey': 'primary',
          'xlsform': 'xlsform',
          'backcheck-dataset': 'backcheck',
          'data-source': 'primary' // default fallback
        };
        return (
          <DataLoader
            onDataLoaded={handleDataLoaded}
            onBackcheckDataLoaded={handleBackcheckDataLoaded}
            onXLSFormLoaded={handleXLSFormLoaded}
            isDataPreviewVisible={!!qualityChecksData}
            onClearPreviousState={clearPreviousState}
            projectId={currentProject.id}
            onNavigateToPreview={() => handleNavigate('datepreview')}
            primaryData={qualityChecksData}
            backcheckData={backcheckData}
            xlsformData={xlsformData}
            initialDatasetType={datasetTypeMap[viewId] || 'primary'}
          />
        );
      }
        
      case 'datepreview':
        if (!qualityChecksData) return <NoDataMessage onNavigate={handleNavigate} message="Load data first to preview it." />;
        return (
          <DataPreview
            primaryData={qualityChecksData}
            backcheckData={backcheckQualityData}
            projectId={currentProject?.id}
            onNavigate={handleNavigate}
          />
        );
        
      case 'data-summary':
        if (!qualityChecksData) return <NoDataMessage onNavigate={handleNavigate} message="Load data first to see the summary." />;
        return (
          <DataSummary
            primaryData={qualityChecksData}
            backcheckData={backcheckQualityData}
            projectId={currentProject?.id}
            primaryFileName={data?.file_name || qualityChecksData?.file_name || 'Primary Survey'}
            backcheckFileName={backcheckData?.file_name || 'Back-Check Data'}
            xlsformData={xlsformData}
          />
        );
        
      case 'correlation-matrix':
        if (!qualityChecksData) return <NoDataMessage onNavigate={handleNavigate} message="Load data first to see correlations." />;
        return (
          <CorrelationMatrix
            primaryData={qualityChecksData}
            backcheckData={backcheckQualityData}
            projectId={currentProject?.id}
            primaryFileName={data?.file_name || qualityChecksData?.file_name || 'Primary Survey'}
            backcheckFileName={backcheckData?.file_name || 'Back-Check Data'}
          />
        );
        
      case 'collector-dashboard':
        if (!qualityChecksData) return <NoDataMessage onNavigate={handleNavigate} message="Load data first to see collector metrics." />;
        return (
          <CollectorDashboard
            primaryData={qualityChecksData}
            backcheckData={backcheckQualityData}
            projectId={currentProject?.id}
            collectorIdColumn={dataCollectorId}
            interviewDateColumn={interviewDateId}
            durationColumn={selectedVariables?.duration?.[0] || null}
            startTimeColumn={null}
            endTimeColumn={null}
            latColumn={selectedVariables?.geolocation?.[0] || null}
            lonColumn={selectedVariables?.geolocation?.[1] || null}
            xlsformData={xlsformData}
            xlsformMatchingResult={xlsformMatchingResult}
          />
        );
        
      case 'configure-back-checks':
        if (!backcheckQualityData) return <NoDataMessage onNavigate={handleNavigate} message="Load back-check data first." />;
        // EXACT props from App.js
        return (
          <ConfigureBackChecks
            backcheckData={backcheckQualityData}
            primaryData={qualityChecksData}
            projectId={currentProject?.id}
            onNavigate={handleNavigate}
            backcheckConfig={backcheckConfig}
            setBackcheckConfig={setBackcheckConfig}
            isLoadingBackcheck={isLoadingBackcheck}
            setIsLoadingBackcheck={setIsLoadingBackcheck}
            backcheckError={backcheckError}
            setBackcheckError={setBackcheckError}
            backcheckResults={backcheckResults}
            setBackcheckResults={setBackcheckResults}
            isExpandedBackcheck={isExpandedBackcheck}
            setIsExpandedBackcheck={setIsExpandedBackcheck}
          />
        );
        
      case 'backcheckdashboard':
        if (!backcheckResults) return <NoDataMessage onNavigate={handleNavigate} message="Run backcheck analysis first." navigateTo="configure-back-checks" />;
        return (
          <BackcheckDashboard
            backcheckResults={backcheckResults}
            primaryData={qualityChecksData}
            backcheckData={backcheckQualityData}
            config={backcheckConfig}
          />
        );
        
      case 'quality-checks':
        if (!qualityChecksData) return <NoDataMessage onNavigate={handleNavigate} message="Load data first to configure quality checks." />;
        // EXACT props from App.js
        return (
          <ConfigureDetectors
            data={qualityChecksData}
            projectId={currentProject?.id}
            columnTypes={effectiveColumnTypes}
            setColumnTypes={setColumnTypes}
            selectedChecks={selectedChecks}
            setSelectedChecks={setSelectedChecks}
            selectedVariables={selectedVariables}
            setSelectedVariables={setSelectedVariables}
            dataCollectorId={dataCollectorId}
            setDataCollectorId={setDataCollectorId}
            respondentId={respondentId}
            setRespondentId={setRespondentId}
            interviewDateId={interviewDateId}
            setInterviewDateId={setInterviewDateId}
            groupByVariable={groupByVariable}
            setGroupByVariable={setGroupByVariable}
            checkResults={checkResults}
            setCheckResults={setCheckResults}
            isLoadingChecks={isLoadingChecks}
            setIsLoadingChecks={setIsLoadingChecks}
            checkError={checkError}
            setCheckError={setCheckError}
            typeValidationIssues={typeValidationIssues}
            setTypeValidationIssues={setTypeValidationIssues}
            onNavigate={handleNavigate}
            isExpandedDQC={isExpandedDQC}
            setIsExpandedDQC={setIsExpandedDQC}
            onBackToModeSelector={handleBackToModeSelector}
            xlsformData={xlsformData}
            textClusteringSettings={textClusteringSettings}
            setTextClusteringSettings={setTextClusteringSettings}
            sentinelSettings={sentinelSettings}
            setSentinelSettings={setSentinelSettings}
            gatedFieldsSettings={gatedFieldsSettings}
            setGatedFieldsSettings={setGatedFieldsSettings}
            probeDepthSettings={probeDepthSettings}
            setProbeDepthSettings={setProbeDepthSettings}
            straightLiningSettings={straightLiningSettings}
            setStraightLiningSettings={setStraightLiningSettings}
            durationRanges={durationRanges}
            setDurationRanges={setDurationRanges}
            setActiveQualityTab={setActiveQualityTab}
          />
        );
        
      case 'dashboard':
        if (!qualityChecksRun) return <NoDataMessage onNavigate={handleNavigate} message="Run quality checks first to see results." navigateTo="quality-checks" />;
        return (
          <DataQualityDashboard
            data={qualityChecksData}
            checkResults={checkResults}
            columnTypes={effectiveColumnTypes}
            dataCollectorId={dataCollectorId}
            onNavigate={handleNavigate}
            activeTab={activeQualityTab}
            onTabChange={setActiveQualityTab}
            selectedVariables={selectedVariables}
            projectId={currentProject?.id}
            isTauri={Boolean(window.__TAURI_INTERNALS__)}
            userInfo={userInfo}
          />
        );
        
      case 'audio-audit': {
        if (!currentProject) return <NoProjectMessage onNavigate={handleNavigate} />;
        // AudioAudit resolves the Tauri API itself via dynamic import
        // (matches the pattern in DataLoader.js for Tauri v2 / withGlobalTauri:false).
        const isTauriEnv = Boolean(window.__TAURI_INTERNALS__);
        if (!isTauriEnv) {
          // Web build: the workspace needs a local media folder and the
          // desktop transcription engine. Explain instead of half-loading.
          return (
            <div style={{ maxWidth: '520px', margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '10px' }}>
                Audio Audits runs in the desktop app
              </h2>
              <p style={{ fontSize: '14px', lineHeight: 1.6, opacity: 0.75, marginBottom: '18px' }}>
                Audio audits work directly with the audio files in a folder on
                your computer and transcribe them on your device. Install the
                DataQuad desktop app, open this project there, and the Audio
                Audit workspace will appear here.
              </p>
              <a href="https://dataquadapp.com/download" target="_blank" rel="noreferrer"
                 style={{ display: 'inline-block', padding: '10px 18px', borderRadius: '10px',
                          background: '#2563EB', color: '#fff', fontSize: '13.5px',
                          fontWeight: 650, textDecoration: 'none' }}>
                Get the desktop app
              </a>
            </div>
          );
        }
        return (
          <AudioAudit
            projectId={currentProject?.id}
            primaryData={qualityChecksData}
            isTauri={isTauriEnv}
            onNavigate={handleNavigate}
            userInfo={userInfo}
          />
        );
      }

      case 'cleaning-studio':
        if (!currentProject) return <NoProjectMessage onNavigate={handleNavigate} />;
        return (
          <CleaningStudio
            data={qualityChecksData}
            columnTypes={effectiveColumnTypes}
            xlsformData={xlsformData}
            projectId={currentProject?.id}
            projectName={currentProject?.name}
            qualityResults={checkResults}
            checkResults={checkResults}
            rawDataStats={{
              totalRows: qualityChecksData?.total_rows || 0,
              totalColumns: Object.keys(effectiveColumnTypes || {}).length,
              columns: qualityChecksData?.columns || Object.keys(effectiveColumnTypes || {})
            }}
            onNavigate={handleNavigate}
            savedCleaningStudioState={cleaningStudioState}
            onSaveCleaningStudioState={setCleaningStudioState}
            onCleaningComplete={async (result) => {
              setCleaningComplete(true);
              if (result.success && result.cleaned_data) {
                setCleanedData({
                  data: result.cleaned_data.slice(0, 100),
                  full_data: result.cleaned_data,
                  total_rows: result.rows_after || result.cleaned_data.length,
                  columns: result.columns || Object.keys(result.cleaned_data[0] || {}),
                  column_types: effectiveColumnTypes || {}
                });
              }
              if (result.success) {
                handleOpenModal('cleaned-review');
              }
            }}
          />
        );
        
      case 'cleaned-review':
        if (!currentProject) return <NoProjectMessage onNavigate={handleNavigate} />;
        return (
          <CleanedDataReview
            projectId={currentProject?.id}
            rawCheckResults={checkResults}
            backcheckResults={backcheckResults}
            onNavigate={handleNavigate}
            onCleanedAnalysisComplete={setCleanedCheckResults}
            selectedChecks={selectedChecks}
          />
        );
        
      case 'data-export':
        if (!currentProject) return <NoProjectMessage onNavigate={handleNavigate} />;
        return (
          <DataExport
            projectId={currentProject?.id}
            projectName={currentProject?.name || 'DataQuad Export'}
            cleanedData={cleanedData}
            onExportComplete={(result) => console.log('Export completed:', result)}
          />
        );
        
      case 'generate-report':
        if (!currentProject) return <NoProjectMessage onNavigate={handleNavigate} />;
        return (
          <ReportGenerator
            projectId={currentProject?.id}
            projectName={currentProject?.name || 'Data Quality Report'}
            rawCheckResults={checkResults}
            cleanedCheckResults={cleanedCheckResults}
            comparisonData={null}
            backcheckResults={backcheckResults}
            rawDataInfo={{
              total_rows: qualityChecksData?.total_rows,
              total_variables: qualityChecksData?.columns?.length
            }}
            cleanedDataInfo={{
              total_rows: cleanedData?.total_rows,
              total_variables: cleanedData?.columns?.length
            }}
            qualityChecksRun={qualityChecksRun}
            cleaningComplete={cleaningComplete}
            backcheckComplete={!!backcheckResults}
            aiAssessmentComplete={mlAssessmentRun}
            onNavigate={handleNavigate}
            onReportGenerated={() => setReportGenerated(true)}
          />
        );
        
      default:
        return (
          <div style={{ padding: '40px', textAlign: 'center', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
            <p>View not found: {viewId}</p>
          </div>
        );
    }
  };
  
  // ============================================
  // RENDER
  // ============================================
  
  if (!userInfo) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh', gap: 20,
        backgroundColor: isDarkMode ? '#0D0E0E' : '#F5F5F5',
        fontFamily: "'Outfit', sans-serif",
      }}>
        <img src={isDarkMode ? logoSourceInverted : logoSource} alt="" style={{ width: 48, height: 48, animation: 'dqPulse 1.8s ease-in-out infinite' }} />
        <style>{`@keyframes dqPulse { 0%, 100% { opacity: 0.4; transform: scale(0.92); } 50% { opacity: 1; transform: scale(1); } }`}</style>
      </div>
    );
  }
  
  const modalConfig = getModalConfig(activeModal);
  
  // ============================================
  // VIEW WRAPPERS
  // Modal and preview render the SAME view; renderViewContent above is the
  // single source of truth. Keeping these as named wrappers preserves
  // existing call sites and makes the preview/modal symmetry explicit.
  // ============================================
  const renderModalContent = () => renderViewContent(activeModal);
  const generatePreviewContent = () => renderViewContent(lastVisitedModal);

  // Handle preview panel click - reopens the last visited modal
  const handlePreviewClick = (modalId) => {
    if (modalId) {
      handleOpenModal(modalId);
    }
  };
  
  return (
    <div style={{ 
      minHeight: '100vh',
      backgroundColor: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    }}>
      {/* Project state loading overlay */}
      {(isRestoringState || isAwaitingRestore) && (
        <ProjectLoadingOverlay
          isDarkMode={isDarkMode}
          message={isAwaitingRestore && !isRestoringState
            ? 'Loading your project'
            : 'Restoring your workspace'}
        />
      )}

      {/* Auto-refresh overlay — shown briefly while we pull fresh data
          from the saved API config after restore completes. Without this,
          the screen flickers as data swaps in silently. */}
      {!isRestoringState && !isAwaitingRestore && isAutoRefreshing && (
        <ProjectLoadingOverlay
          isDarkMode={isDarkMode}
          message="Refreshing your data"
          substep={autoRefreshError
            ? `Retrying — ${autoRefreshError}`
            : 'Pulling latest from API…'}
        />
      )}
      
      {/* Invite Acceptance Banner */}
      {pendingInvite && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
          background: isDarkMode
            ? 'linear-gradient(135deg, #1a2332, #0f1923)'
            : 'linear-gradient(135deg, #EFF6FF, #DBEAFE)',
          borderBottom: `1px solid ${isDarkMode ? 'rgba(56,182,255,0.2)' : 'rgba(56,182,255,0.3)'}`,
          padding: '14px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px',
          fontFamily: "'Outfit', sans-serif",
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        }}>
          <span style={{
            fontSize: '14px',
            color: isDarkMode ? '#E2E8F0' : '#1E293B',
          }}>
            <strong style={{ color: '#4FC3F7' }}>Team Invitation</strong>
            {pendingInvite.invitations?.[0]?.organization_name
              ? ` — You've been invited to join ${pendingInvite.invitations[0].organization_name}`
              : ' — You have a pending team invitation'}
          </span>
          <button
            onClick={() => handleAcceptInvite(pendingInvite.token)}
            disabled={inviteProcessing}
            style={{
              padding: '6px 18px', borderRadius: '6px', border: 'none',
              background: '#4FC3F7', color: '#FFF', fontSize: '13px',
              fontWeight: '600', cursor: 'pointer',
              opacity: inviteProcessing ? 0.6 : 1,
            }}
          >
            {inviteProcessing ? 'Joining...' : 'Accept'}
          </button>
          <button
            onClick={handleDismissInvite}
            style={{
              padding: '6px 14px', borderRadius: '6px',
              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
              background: 'transparent',
              color: isDarkMode ? '#94A3B8' : '#64748B',
              fontSize: '13px', cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Pipeline Navigation Background */}
      <PipelineNavigation
        currentView={activeModal}
        onNavigate={handleNavigate}
        onOpenModal={handleOpenModal}
        onSelectModal={handleSelectModal}
        activeModal={activeModal}
        projectLoaded={!!currentProject}
        projectName={currentProject?.name || null}
        dataLoaded={dataLoaded}
        qualityChecksRun={qualityChecksRun}
        mlAssessmentRun={mlAssessmentRun}
        cleaningComplete={cleaningComplete}
        reportGenerated={reportGenerated}
        backcheckDataLoaded={backcheckDataLoaded}
        backcheckConfigured={isBackcheckConfigured()}
        hasAudio={hasAudioColumns}
        checkResults={checkResults}
        userName={userInfo?.name}
        userPicture={userInfo?.picture}
        onLogout={handleLogout}
        previewContent={generatePreviewContent()}
        previewModalId={lastVisitedModal}
        onPreviewClick={handlePreviewClick}
        onProjectSelect={handleProjectSelect}
      />
      
      {/* Content Modal */}
      <ContentModal
        isOpen={!!activeModal}
        onClose={handleCloseModal}
        title={modalConfig.title}
        subtitle={modalConfig.subtitle}
        icon={modalConfig.icon}
        iconColor={modalConfig.iconColor}
        maxWidth={modalConfig.maxWidth}
        hideHeader={modalConfig.hideHeader}
      >
        {renderModalContent()}
      </ContentModal>
      
      {/* Feedback Button */}
      {userInfo && (
        <FeedbackButton
          userInfo={userInfo}
          currentView={activeModal || lastVisitedModal}
          currentProjectId={currentProject?.id}
        />
      )}
    </div>
  );
}

// Helper Components
const NoDataMessage = ({ onNavigate, message, navigateTo = 'primary-survey' }) => {
  const { isDarkMode } = useTheme();
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '400px',
      textAlign: 'center',
      color: isDarkMode ? '#9CA3AF' : '#6B7280',
      padding: '2rem'
    }}>
      <Database size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
      <h2 style={{ fontSize: '24px', marginBottom: '16px' }}>No Data Available</h2>
      <p style={{ fontSize: '16px', marginBottom: '24px' }}>{message}</p>
      <button
        onClick={() => onNavigate(navigateTo)}
        style={{
          padding: '12px 24px',
          backgroundColor: '#4FC3F7',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600'
        }}
      >
        {['primary-survey', 'xlsform', 'backcheck-dataset', 'data-source'].includes(navigateTo) ? 'Load Data' : 'Go to ' + navigateTo.replace(/-/g, ' ')}
      </button>
    </div>
  );
};

const NoProjectMessage = ({ onNavigate }) => {
  const { isDarkMode } = useTheme();
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '400px',
      textAlign: 'center',
      color: isDarkMode ? '#9CA3AF' : '#6B7280',
      padding: '2rem'
    }}>
      <FolderOpen size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
      <h2 style={{ fontSize: '24px', marginBottom: '16px' }}>No Project Selected</h2>
      <p style={{ fontSize: '16px', marginBottom: '24px' }}>
        Please select or create a project first.
      </p>
      <button
        onClick={() => onNavigate('projects')}
        style={{
          padding: '12px 24px',
          backgroundColor: '#4FC3F7',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600'
        }}
      >
        Go to Projects
      </button>
    </div>
  );
};

export default App;